// qs-useColorDrag.ts
import { hslToRgb } from '@/utils/qs-util-colorUtils'
import { ref, type Ref } from 'vue'

type DragType = 'saturation' | 'hue' | null
type EventHandler = (e: MouseEvent | TouchEvent) => void
type EndHandler = () => void

export function useColorDrag(
  red: Ref<number>,
  green: Ref<number>,
  blue: Ref<number>,
  hue: Ref<number>,
  saturation: Ref<number>,
  lightness: Ref<number>,
  emit: (event: 'update:modelValue', value: string) => void
) {
  const isDragging = ref(false)
  const dragType = ref<DragType>(null)

  const getElementPosition = (event: MouseEvent | TouchEvent, element: Element): { x: number; y: number } => {
    const rect = element.getBoundingClientRect()
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY

    return {
      x: Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (clientY - rect.top) / rect.height))
    }
  }

  const preventTouchScrolling = (event: MouseEvent | TouchEvent): void => {
    if ('touches' in event) {
      event.preventDefault()
    }
  }

  const updateColorFromHSL = (): void => {
    const rgb = hslToRgb(hue.value, saturation.value, lightness.value)
    red.value = rgb.r
    green.value = rgb.g
    blue.value = rgb.b
  }

  const emitColorUpdate = (): void => {
    const hexColor = `#${red.value.toString(16).padStart(2, '0')}${green.value.toString(16).padStart(2, '0')}${blue.value.toString(16).padStart(2, '0')}`
    emit('update:modelValue', hexColor)
  }

  const addEventListeners = (handleMove: EventHandler, handleEnd: EndHandler): void => {
    document.addEventListener('mousemove', handleMove, { passive: true })
    document.addEventListener('mouseup', handleEnd, { passive: true })

    if ('ontouchstart' in window) {
      document.addEventListener('touchmove', handleMove, { passive: false })
      document.addEventListener('touchend', handleEnd, { passive: true })
    }
  }

  const removeEventListeners = (handleMove: EventHandler, handleEnd: EndHandler): void => {
    document.removeEventListener('mousemove', handleMove)
    document.removeEventListener('mouseup', handleEnd)
    document.removeEventListener('touchmove', handleMove)
    document.removeEventListener('touchend', handleEnd)
  }

  const handleSaturationDrag = (event: MouseEvent | TouchEvent): void => {
    if (!isDragging.value || dragType.value !== 'saturation') return

    preventTouchScrolling(event)

    const saturationElement = document.querySelector('.color-picker-saturation')
    if (!saturationElement) return

    const { x, y } = getElementPosition(event, saturationElement)

    saturation.value = x
    lightness.value = 1 - y

    updateColorFromHSL()
    emitColorUpdate()
  }

  const handleHueDrag = (event: MouseEvent | TouchEvent): void => {
    if (!isDragging.value || dragType.value !== 'hue') return

    preventTouchScrolling(event)

    const hueElement = document.querySelector('.color-picker-hue')
    if (!hueElement) return

    const { x } = getElementPosition(event, hueElement)
    hue.value = x * 360

    updateColorFromHSL()
    emitColorUpdate()
  }

  const endDrag = (): void => {
    isDragging.value = false
    dragType.value = null
  }

  const startSaturationDrag = (event: MouseEvent | TouchEvent): void => {
    isDragging.value = true
    dragType.value = 'saturation'
    handleSaturationDrag(event)

    const handleMove: EventHandler = (e) => handleSaturationDrag(e)
    const handleEnd: EndHandler = () => {
      endDrag()
      emitColorUpdate()
      removeEventListeners(handleMove, handleEnd)
    }

    addEventListeners(handleMove, handleEnd)
  }

  const startHueDrag = (event: MouseEvent | TouchEvent): void => {
    isDragging.value = true
    dragType.value = 'hue'
    handleHueDrag(event)

    const handleMove: EventHandler = (e) => handleHueDrag(e)
    const handleEnd: EndHandler = () => {
      endDrag()
      emitColorUpdate()
      removeEventListeners(handleMove, handleEnd)
    }

    addEventListeners(handleMove, handleEnd)
  }

  return {
    isDragging,
    dragType,
    startSaturationDrag,
    startHueDrag,
    handleSaturationDrag,
    handleHueDrag
  }
}